export function safeExperienceDestination(
  value: string | undefined,
): string | null {
  if (!value) return null;
  if (
    /[\s\\]/.test(value) ||
    Array.from(value).some(
      (character) =>
        character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
    )
  )
    return null;
  if (/^\/(?!\/)/.test(value)) return value;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}

/** Offers embed only the video providers already used by the member area. */
export function experienceVideoEmbed(value: string): string | null {
  if (!safeExperienceDestination(value)?.startsWith('https://')) return null;
  const url = new URL(value);
  const host = url.hostname;
  if (
    [
      'youtube.com',
      'www.youtube.com',
      'youtube-nocookie.com',
      'www.youtube-nocookie.com',
      'youtu.be',
    ].includes(host)
  ) {
    const id =
      host === 'youtu.be'
        ? url.pathname.slice(1)
        : url.pathname.startsWith('/embed/')
          ? url.pathname.slice(7)
          : url.pathname === '/watch'
            ? url.searchParams.get('v')
            : null;
    return id && /^[\w-]{11}$/.test(id)
      ? `https://www.youtube-nocookie.com/embed/${id}`
      : null;
  }
  if (['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(host)) {
    const id = url.pathname.match(/^\/(?:video\/)?(\d+)$/)?.[1];
    if (!id) return null;
    const embed = new URL(`https://player.vimeo.com/video/${id}`);
    const hash = url.searchParams.get('h');
    if (hash) embed.searchParams.set('h', hash);
    return embed.href;
  }
  if (host === 'play.gumlet.io' && /^\/embed\/[a-f\d]{24}$/.test(url.pathname))
    return `${url.origin}${url.pathname}`;
  return null;
}

export function experienceError(error: unknown, fallback: string): string {
  const response = error as {
    response?: {
      data?: {
        message?: string | string[];
        errors?: Array<{ message?: string }>;
      };
    };
  };
  const errors = response?.response?.data?.errors;
  if (Array.isArray(errors)) {
    const details = errors
      .map(detail => detail?.message)
      .filter((message): message is string => typeof message === 'string' && message.length > 0);
    if (details.length) return details.join(' ');
  }
  const message = response?.response?.data?.message;
  return Array.isArray(message)
    ? message.join(' ')
    : message || (error instanceof Error ? error.message : fallback);
}

export function contentAccessReason(reason: string): string {
  const labels: Record<string, string> = {
    granted: 'Acesso concedido para este perfil.',
    not_owned: 'Este perfil não possui um acesso válido ao curso.',
    group_required: 'Falta um dos grupos exigidos para este conteúdo.',
    expired: 'O acesso a este conteúdo expirou.',
    unpublished: 'Este conteúdo ainda não está publicado.',
    invalid_policy:
      'A regra de acesso precisa ser corrigida antes da liberação.',
    invalid_hierarchy: 'A organização dos módulos precisa ser corrigida.',
    not_found: 'O conteúdo não foi encontrado.',
  };
  return labels[reason] || reason;
}
